import Button from '@aurora/shared-client/components/common/Button/Button';
import { ButtonVariant } from '@aurora/shared-client/components/common/Button/enums';
import { IconColor, IconSize } from '@aurora/shared-client/components/common/Icon/enums';
import Icon from '@aurora/shared-client/components/common/Icon/Icon';
import Icons from '@aurora/shared-client/icons';
import type { Attachment } from '@aurora/shared-generated/types/graphql-schema-types';
import { EndUserComponent } from '@aurora/shared-types/pages/enums';
import React, { useRef } from 'react';
import { OverlayTrigger, Popover, ProgressBar, useClassNameMapper } from 'react-bootstrap';
import { useHoverDirty } from 'react-use';
import { useUIDSeed } from 'react-uid';
import type { AttachmentViewVariant, ItemType } from '../../../../types/enums';
import StrongTag from '@aurora/shared-client/components/common/StrongTag/StrongTag';
import type { ItemViewVariantFC } from '../../../entities/types';
import ImageOption from '../../../images/ImagePreviewModal/ImageOption';
import useTranslation from '../../../useTranslation';
import type AttachmentOption from '@aurora/shared-client/helpers/attachment/AttachmentOption';
import { asAttachmentOption } from '@aurora/shared-client/helpers/attachment/AttachmentOption';
import { transformFileSize } from '@aurora/shared-client/helpers/attachment/AttachmentHelper';
import localStyles from './AttachmentViewChip.module.pcss';

/*
 * Attachment View - Chip
 *
 * @constructor
 * @author Dolan Halbrook, Willi Hyde
 */
const AttachmentViewChip: ItemViewVariantFC<
  Attachment | AttachmentOption,
  ItemType.ATTACHMENT,
  AttachmentViewVariant.CHIP
> = ({
  entity: attachment,
  className,
  canEdit,
  onRemove,
  onImageAttachmentClick,
  isPreview = true
}) => {
  const cx = useClassNameMapper(localStyles);
  const uidSeed = useUIDSeed();
  const {
    FormattedMessage,
    intl,
    formatMessage,
    loading: textLoading
  } = useTranslation(EndUserComponent.ATTACHMENT_VIEW_CHIP);
  const { locale } = intl;
  const option = asAttachmentOption(attachment);
  const { url, name, size = 0, contentType, loading = false, errors = [] } = option;
  const attachmentPreviewReference = useRef(null);
  const isHovering = useHoverDirty(attachmentPreviewReference);
  const isImage = contentType && contentType.startsWith('image/');
  const isPdf = contentType === 'application/pdf';
  const isPreviewable = isImage || isPdf;

  /**
   * Handles the click event on image `AttachmentChip`.
   *
   * @param event Mouse event
   */
  function handleImageAttachmentClick(event: React.MouseEvent) {
    event.preventDefault();
    const selectedImageOption = new ImageOption(attachment);
    onImageAttachmentClick(selectedImageOption);
  }

  const renderErrorPopover = (
    <Popover id={uidSeed('attachment-error-popover')}>
      <Popover.Content className={cx('lia-attachment-popover-content')}>
        {errors.map(error => (
          <span className={cx('lia-attachment-popover-text')} key={error.id}>
            <FormattedMessage
              id={error.id}
              values={{
                strong: StrongTag,
                ...error.values
              }}
            />
          </span>
        ))}
      </Popover.Content>
    </Popover>
  );

  function renderMetaContent(): React.ReactElement {
    if (errors.length > 0) {
      return (
        <span className={cx('lia-attachment-error lia-attachment-text')} role="alert">
          <Icon
            icon={Icons.InfoIcon}
            size={IconSize.PX_12}
            color={IconColor.DANGER}
            className={cx('lia-attachment-error-icon')}
          />
          <span>{formatMessage('errorTitle')}</span>
        </span>
      );
    }

    if (loading) {
      return (
        <div className={cx('lia-loading-bar-wrap')}>
          <ProgressBar
            className={cx('lia-loading-bar')}
            now={100}
            title={formatMessage('loading')}
          />
        </div>
      );
    }

    return (
      <span className={cx('lia-attachment-meta lia-attachment-text')}>
        {isHovering && isPreviewable && isPreview
          ? formatMessage('previewFile')
          : transformFileSize(size, locale)}
      </span>
    );
  }

  function renderUrlIfExists(): React.ReactElement {
    const attachmentDetails = (
      <>
        <span className={cx('lia-attachment-filename lia-attachment-text')}>{name}</span>
        {renderMetaContent()}
      </>
    );

    if (url && isPreviewable) {
      return (
        <Button
          variant={isPreview ? ButtonVariant.LINK : ButtonVariant.NO_VARIANT}
          className={cx(
            'lia-attachment-content',
            { 'lia-attachment-preview': !isPreview },
            className
          )}
          onClick={
            onImageAttachmentClick && isPreview
              ? event => handleImageAttachmentClick(event)
              : undefined
          }
          ref={attachmentPreviewReference}
          data-testid="AttachmentViewChip.ImageURL"
        >
          {attachmentDetails}
        </Button>
      );
    } else if (url) {
      return (
        <a
          href={isPreview && url}
          className={cx(
            'lia-attachment-content',
            { 'lia-attachment-preview': !isPreview },
            className
          )}
          data-testid="AttachmentViewChip.DocumentURL"
        >
          {attachmentDetails}
        </a>
      );
    }

    const attachmentDetailsWrap = (
      <div className={cx('lia-attachment-content', className)}>{attachmentDetails}</div>
    );

    if (errors.length > 0) {
      return (
        <OverlayTrigger placement="auto" overlay={renderErrorPopover}>
          {attachmentDetailsWrap}
        </OverlayTrigger>
      );
    }

    return attachmentDetailsWrap;
  }

  if (textLoading) {
    return null;
  }

  return (
    <div className={cx('lia-attachment', className)}>
      {renderUrlIfExists()}
      {canEdit && (
        <Button
          variant={ButtonVariant.LINK}
          className={cx('lia-attachment-action')}
          onClick={async (): Promise<void> => onRemove(option)}
          aria-label={formatMessage('removeFile', { name })}
        >
          <Icon
            icon={Icons.CloseIcon}
            size={IconSize.PX_12}
            data-testid="AttachmentViewChip.CloseIcon"
          />
        </Button>
      )}
      {!canEdit && (
        <a
          href={url}
          className={cx('lia-attachment-action')}
          aria-label={formatMessage('downloadFile', { name })}
        >
          <Icon icon={Icons.DownloadIcon} size={IconSize.PX_16} />
        </a>
      )}
    </div>
  );
};
export default AttachmentViewChip;
